import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel,
  FormGroup, FormHelperText, InputBase, InputLabel, Link, Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import addToWatchlist from "../../../../lib/watchlist/_addToWatchlist";
import getWatchlist from "../../../../lib/watchlist/_getWatchlist";
import useAuth from "../../../hook/auth";
import { LoadingButton } from "@mui/lab";
import deleteFromWatchlist from "../../../../lib/watchlist/_deleteFromWatchlist";

const WatchlistInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: 6,
    position: 'relative',
    backgroundColor: theme.palette.mode === 'light' ? '#F3F6F9' : '#1A2027',
    border: '1px solid',
    borderColor: theme.palette.mode === 'light' ? '#E0E3E7' : '#2D3843',
    fontSize: '.875rem',
    width: '100%',
    padding: '10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    '&:focus': {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
    },
  },
}));

const AddToWatchlistMenu = (props) => {
  const { user } = useAuth();
  const { ticker, _watchlists, setWatchlists } = props || {};
  const [create, setCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingItem, setLoadingItem] = useState(null);
  const [error, setError] = useState(null);

  const refreshWatchlists = useCallback(async () => {
    if (user?.accessToken) {
      try {
        const data = await getWatchlist(user.accessToken);
        const { getWatchlist: lists } = data || {};
        setWatchlists(lists ?? []);
      } catch (err) {
        console.error('Failed to refresh watchlists:', err);
      }
    }
  }, [user?.accessToken, setWatchlists]);

  useEffect(() => {
    refreshWatchlists();
  }, [user?.accessToken]);

  const handleWatchlistCheck = async (event, name) => {
    const isChecked = event.target.checked;
    setLoadingItem(name);
    setError(null);
    try {
      if (isChecked) {
        await addToWatchlist(name, [ticker], "", "", null, user?.accessToken);
      } else {
        await deleteFromWatchlist(name, ticker, user?.accessToken);
      }
      await refreshWatchlists();
    } catch (err) {
      console.error('Watchlist update failed:', err);
      setError(`Failed to ${isChecked ? 'add to' : 'remove from'} "${name}"`);
    } finally {
      setLoadingItem(null);
    }
  };

  const handleCreateWatchlist = async (event) => {
    event.preventDefault();
    if (!user?.accessToken || !newName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const _columns = "['ticker','tickername','sector','MKTCAP','REV_TTM','PCHG__7','PCHG__30','PCHG__365','PE','PS','ROE','REV_TTM__PCHG_3Y','DRAWDN']";
      await addToWatchlist(newName.trim(), [ticker], "", "", _columns, user.accessToken);
      await refreshWatchlists();
      setNewName('');
      setCreate(false);
    } catch (err) {
      console.error('Create watchlist failed:', err);
      setError('Failed to create watchlist');
    } finally {
      setLoading(false);
    }
  };

  const tickerInWatchlist = (d) => {
    try { return d?.tickers?.includes(`"${ticker}"`); }
    catch { return false; }
  };

  return (
    <>
      {user?.accessToken ?
        <Box sx={{ px: 2, pt: 2, pb: 0.5, minWidth: 260 }}>
          <Typography variant="subtitle2" sx={{ fontSize: '.8rem', fontWeight: 600, color: 'text.secondary', mb: 1, letterSpacing: '0.01em' }}>
            Add <Typography component="span" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '.8rem' }}>{ticker}</Typography> to watchlist
          </Typography>
          <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
            <FormGroup sx={{ maxHeight: 200, overflow: 'auto', mx: -0.5 }}>
              {_watchlists && _watchlists.length > 0 ? _watchlists.map((d) => {
                const isInList = tickerInWatchlist(d);
                const isItemLoading = loadingItem === d?.name;
                return (
                  <FormControlLabel
                    key={d?.name}
                    disabled={isItemLoading}
                    sx={{
                      mx: 0, borderRadius: '8px', py: 0.25, px: 0.5,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                      '& .MuiFormControlLabel-label': {
                        fontSize: '.875rem', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px',
                      },
                    }}
                    control={
                      isItemLoading
                        ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38 }}>
                            <CircularProgress size={18} />
                          </Box>
                        : <Checkbox size="small" checked={isInList} name={d?.name} />
                    }
                    label={d?.name}
                    onChange={(e) => handleWatchlistCheck(e, d?.name)}
                  />
                );
              })
                : <FormControlLabel
                    control={<Checkbox size="small" checked={false} name={"My Watchlist"} />}
                    label={"My Watchlist"}
                    sx={{ mx: 0, '& .MuiFormControlLabel-label': { fontSize: '.875rem' } }}
                    onChange={(e) => handleWatchlistCheck(e, "My Watchlist")}
                  />}
            </FormGroup>
          </FormControl>
          {error && <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5, fontSize: '.75rem' }}>{error}</Typography>}
          <FormHelperText sx={{ mt: 1, mb: 0.5, fontSize: '.75rem' }}>
            Manage lists on the <Link href="/watchlists" underline="hover">watchlists page</Link>.
          </FormHelperText>
        </Box>
        : <FormHelperText sx={{ px: 2, my: 1.5 }}>Please <Link href="/auth" underline="hover">sign in</Link> to manage your watchlists.</FormHelperText>}
      <Divider />
      {!create &&
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          sx={{ textTransform: 'none', py: 1, px: 2, width: '100%', borderRadius: '0 0 4px 4px', justifyContent: 'start', fontSize: '.875rem' }}
          onClick={() => setCreate(true)}
        >
          New Watchlist
        </Button>}
      {create &&
        <Box
          component="form"
          noValidate
          sx={{ px: 2, pt: 1.5, pb: 1.5 }}
          onSubmit={handleCreateWatchlist}
        >
          <FormControl variant="standard" sx={{ width: '100%' }}>
            <InputLabel shrink htmlFor="watchlist-input">
              Create new
            </InputLabel>
            <WatchlistInput
              size="small"
              disabled={!user?.accessToken}
              placeholder="Eg., My Watchlist"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              inputProps={{ 'aria-label': 'New watchlist name' }}
              id="watchlist-input"
              autoFocus
            />
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
            <Button size="small" sx={{ textTransform: 'none' }} onClick={() => { setCreate(false); setNewName(''); }}>Cancel</Button>
            <LoadingButton
              sx={{ textTransform: 'none', ml: 'auto' }}
              type="submit"
              disabled={newName.trim() === '' || !user?.accessToken}
              loading={loading}
              loadingIndicator="Creating..."
              size="small"
              variant="contained"
            >
              Create
            </LoadingButton>
          </Box>
        </Box>}
    </>
  );
}

export default AddToWatchlistMenu
